import { SearchOutlined, DownOutlined, FilterOutlined } from '@ant-design/icons'
import { Table, Input, Space, Card, Statistic, Button, Row, Col, Typography, Badge, Skeleton, Progress, Collapse, Divider, Dropdown, Popconfirm, Tooltip } from 'antd'
import _ from 'lodash'
import moment from 'moment'
import { useEffect, useState, useContext } from 'react'
import Highlighter from 'react-highlight-words'
import { CSVLink } from 'react-csv'
import DateRangeSelector from '../dateRangeSelector'
import { UserContext } from '../../contexts/userContext'
import { db, firestore } from '../../firebase'
import { sourceNameLookup, affIdHashedLookup } from '../../helpers/sources'
import { LeadShareParterModal } from '../modals/leadSharePartnerModal'

const { Title, Text } = Typography

const LeadsShared = () => {

  const [isFetching, setIsFetching] = useState(false)
  const [leadShares, setLeadShares] = useState([])
  const [leadSharesPaused, setLeadSharesPaused] = useState([])
  const [leadSharesDeleted, setLeadSharesDeleted] = useState([])
  const [fetchStartDate, setFetchStartDate] = useState()
  const [fetchEndDate, setFetchEndDate] = useState()
  const [isFetchingTable, setIsFetchingTable] = useState(false)
  const [historyTable, setHistoryTable] = useState([])
  const [filteredTable, setFilteredTable] = useState([])
  const [filters, setFilters] = useState({ shared_to: undefined, received_from: undefined, offer: undefined })
  const [groupedPartners, setGroupedPartners] = useState([])
  const [groupedReceived, setGroupedReceived] = useState([])
  const [partnerTotals, setPartnerTotals] = useState({});
  const [receivedTotals, setReceivedTotals] = useState({});
  const [totalReceived, setTotalReceived] = useState()
  const [totalSent, setTotalSent] = useState()
  const [totalReceivedAndSent, setTotalReceivedAndSent] = useState()
  const [totalUniqueSent, setTotalUniqueSent] = useState()
  const [totalUniqueSentPercent, setTotalUniqueSentPercent] = useState()
  const [searchText, setSearchText] = useState(null)
  const [searchedColumn, setSearchedColumn] = useState(null)
  const { user } = useContext(UserContext)
  const isTestUser = process.env.REACT_APP_TEST_USERS?.includes(user.email)
  const [editPartner, setEditPartner] = useState(null);

  useEffect(() => {

    // fetch data
    handleFetchLeadShare()

  }, [])

  useEffect(() => {

    if (fetchStartDate && fetchEndDate) {
      handleFetchLeadShare()
      fetchHistory()
    }

  }, [fetchStartDate, fetchEndDate])

  const applyFilters = ((historyTable, filters = {}) => {
    // console.log('applyFilters', { historyTable })

    const filteredBySharedTable = historyTable.filter((row) => !filters.shared_to || filters.shared_to.includes(row.full_name))
    const filteredByReceivedTable = historyTable.filter((row) => !filters.received_from || filters.received_from.includes(row.received_from))
    const filteredTable = historyTable.filter((row) => (
      (!filters.shared_to || filters.shared_to.includes(row.full_name)) &&
      (!filters.received_from || filters.received_from.includes(row.received_from)) &&
      (!filters.offer || filters.offer.includes(row.offer))
    ))

    // console.log('applyFilters', { filteredTable, filteredByReceivedTable, filteredBySharedTable })

    // grouped partners
    const groupedFilteredShared = filteredByReceivedTable
      .reduce((acc, obj) => {

        const category = `${obj.shared_to}-${obj.entity}`
        const existingArray = acc.find((arr) => `${arr[0]?.shared_to}-${arr[0]?.entity}` === category)

        if (existingArray) {
          existingArray.push(obj)
        } else {
          acc.push([obj])
        }

        return acc
      }, [])

    const partnerTotals = groupedFilteredShared
      .map((g) => [g[0].full_name, { total: g.length }])
      .reduce((o, [k, v]) => Object.assign(o, { [k]: v }), {});

    const groupedShared = historyTable
      .reduce((acc, obj) => {

        const category = `${obj.shared_to}-${obj.entity}`
        const existingArray = acc.find((arr) => `${arr[0]?.shared_to}-${arr[0]?.entity}` === category)

        if (existingArray) {
          existingArray.push(obj)
        } else {
          acc.push([obj])
        }

        return acc
      }, [])

    // grouped received from
    const groupedFilteredReceived = filteredBySharedTable
      .reduce((acc, obj) => {

        const category = obj.received_from
        const existingArray = acc.find((arr) => arr[0]?.received_from === category)

        if (existingArray) {
          existingArray.push(obj)
        } else {
          acc.push([obj])
        }

        return acc
      }, [])

    const receivedTotals = groupedFilteredReceived
      .map((g) => {
        const unique = determineAllUnqiue(g);
        return [g[0].received_from, { total: g.length, unique, uniquePct: unique / g.length * 100 }]
      })
      .reduce((o, [k, v]) => Object.assign(o, { [k]: v }), {});

    const groupedReceived = historyTable
      .reduce((acc, obj) => {

        const category = obj.received_from
        const existingArray = acc.find((arr) => arr[0]?.received_from === category)

        if (existingArray) {
          existingArray.push(obj)
        } else {
          acc.push([obj])
        }

        return acc
      }, [])

    setHistoryTable(historyTable)
    setFilteredTable(filteredTable)
    setGroupedPartners(groupedShared)
    setGroupedReceived(groupedReceived)
    setPartnerTotals(partnerTotals);
    setReceivedTotals(receivedTotals);

    const totalReceived = historyTable
      .filter((row) => !filters.shared_to || filters.shared_to.includes(row.full_name))
      .length
    const totalSent = historyTable
      .filter((row) => !filters.received_from || filters.received_from.includes(row.received_from))
      .length

    setTotalReceived(totalReceived)
    setTotalSent(totalSent)
    setTotalReceivedAndSent(filteredTable.length)

    const totalUniqueSent = determineAllUnqiue(filteredTable)

    setTotalUniqueSent(totalUniqueSent)
    setTotalUniqueSentPercent(filteredTable.length ? totalUniqueSent / filteredTable.length * 100 : 0)

    // console.log('applyFilters', { filteredTable, groupedFilteredShared, filteredGroupReceivedTotals: receivedTotals, totalReceived })
  })

  const toggleFilter = (name, value) => {
    const f = { ...filters }
    let updatedFilters

    if (value === undefined || Array.isArray(value)) {
      // select all or clear
      updatedFilters = { ...f, [name]: value }
    } else if (!filters[name]) {
      // no filter yet

      // add the first filter
      // updatedFilters = { ...f, [name]: [value] }

      // add all but this one
      const values = name === 'shared_to'
        ? groupedPartners.map((x) => x[0].full_name).filter((x) => x != value)
        : groupedReceived.map((x) => x[0][name]).filter((x) => x != value)
      updatedFilters = { ...f, [name]: values }
    } else if (filters[name]?.includes(value)) {
      // remove
      let values = filters[name].filter((x) => x !== value)
      // if (values.length === 0) values = null

      updatedFilters = { ...f, [name]: values }
    } else {
      // add
      const values = [...f[name], value]

      updatedFilters = { ...f, [name]: values.length === (name === 'shared_to' ? groupedPartners : groupedReceived).length ? null : values }
    }

    setFilters(updatedFilters)
    applyFilters(historyTable, updatedFilters)
  }

  const resetFilters = () => {
    setFilters({ shared_to: undefined, received_from: undefined })
  }

  const fetchHistory = async () => {

    setIsFetchingTable(true)

    try {

      // list of lead shares
      const res = await firestore.collection('leadShare')
        .where('created_at', '>=', new Date(fetchStartDate.startOf('d').valueOf()))
        .where('created_at', '<=', new Date(fetchEndDate.endOf('d').valueOf()))
        .orderBy('created_at', 'desc')
        .get()

      if (res.empty) {
        setHistoryTable([])
        setFilteredTable([])
        return
      }

      const tableData = res.docs.map((x, i) => ({
        key: i,
        created_at: moment(x.data().created_at.toDate()).format('MM/DD/YY hh:mm A'),
        entity: (x.data()?.entity || x.data().shared_to === 'Schaeffers') ? 'CJ' : 'BTM',
        full_name: `${x.data()?.entity ? x.data()?.entity : 'BTM'} - ${x.data().shared_to}`,
        shared_to: x.data().shared_to,
        received_from: sourceNameLookup(x.data()),
        affIdHashed: affIdHashedLookup[x.data().affId],
        affiliate_id: x.data().affId,
        offer: x.data().offer,
        email: x.data().email,
        leadShare_value: ((x.data().leadShare_value || 0) / 100).toFixed(2),
      }))

      applyFilters(tableData, filters)

    } catch (error) {

      console.error({ error })

    } finally {

      // remove loading
      setIsFetchingTable(false)
    }
  }

  const handleFetchLeadShare = async () => {

    // set fetching
    setIsFetching(true)

    try {

      // fetch lead shares
      const shareRes = await firestore.collection('altaSend').get()
      const shares = shareRes.docs.map(x => ({
        ...x.data(),
        id: x.id,
        status: (x.data().status === 'active'
          && moment().diff(moment(x.data().last_timestamp?.toDate() || 0)) >= 0
          && x.data().count < (x.data().max_daily + (x.data().max_daily_extra || 0))
        ) ? 'sending' : x.data().status,
        percent: (x.data().count < (x.data().max_daily + (x.data().max_daily_extra || 0)) && x.data().status === 'active') ? Math.floor((x.data().count / (x.data().max_daily + (x.data().max_daily_extra || 0))) * 100) : 0
      }))

      // reorganize
      const sending = shares.filter(x => x.status === 'sending').sort((a, b) => b.count - a.count)
      const active = shares.filter(x => x.status === 'active').sort((a, b) => moment(a.last_timestamp.toDate()).valueOf() - moment(b.last_timestamp.toDate()).valueOf())
      const paused = shares.filter(x => x.status === 'paused').sort((a, b) => moment(b.last_timestamp.toDate()).valueOf() - moment(a.last_timestamp.toDate()).valueOf())
      const deleted = shares.filter(x => x.status === 'deleted').sort((a, b) => moment(b.last_timestamp.toDate()).valueOf() - moment(a.last_timestamp.toDate()).valueOf())
      const allShares = [...sending, ...active]

      setLeadShares(allShares)
      setLeadSharesPaused(paused)
      setLeadSharesDeleted(deleted)

    } catch (error) {

      console.log(error)


    } finally {

      // remove loading
      setIsFetching(false)
    }
  }

  const handleRefresh = () => {

    handleFetchLeadShare()
    if (fetchStartDate && fetchEndDate) fetchHistory()
  }

  const handleUpdatePartner = async (partnerId, toUpdate) => {
    // remove display logic data
    const isNew = !partnerId
    partnerId ||= _.camelCase(toUpdate.name)
    const { id, percent, ...toUpdateCleaned } = toUpdate
    // console.log('handleUpdatePartner', { toUpdateCleaned })

    const doc = firestore.collection('altaSend').doc(partnerId)
    const res = await doc.get()

    if (isNew) {
      if (res.exists) throw Error(`A leadShare with id ${partnerId} (derived from name) already exists: pick another name`)

      await doc.set(toUpdateCleaned)
    } else {
      await doc.update(toUpdateCleaned)
    }

    handleRefresh()
  }

  const hoursSinceLast = (data) => {
    const lastTimestamp = data.last_timestamp
    const lastTimestampToDate = lastTimestamp?.toDate() || 0
    const currentMoment = moment()
    const lastTimestampMoment = moment(lastTimestampToDate)
    const hours = currentMoment.diff(lastTimestampMoment, 'hours')

    return hours
  }

  const handleActivatePartner = async (data) => {

    // only reset last_timestamp when paused before today (ie can pause/activate on the same day without re-queueing)
    handleUpdatePartner(data.id, {
      status: 'active',
      ...hoursSinceLast(data) > 24 && {
        last_timestamp: new db.firestore.Timestamp(
          moment().add(-1, 'day')
            .hour(data.last_timestamp.toDate().getHours())
            .minute(data.last_timestamp.toDate().getMinutes()).valueOf() / 1000
          , 0)
      }
    })
  }

  const handleSendNowPartner = async (data) => {

    handleUpdatePartner(data.id, {
      status: 'active',
      count: 0,
      last_timestamp: db.firestore.FieldValue.serverTimestamp()
    })
  }

  const handleSendMorePartner = async (data) => {
    const num = prompt(`Number of leads to send in excess of ${data.max_daily || 0} (blank to reset)`, data.max_daily_extra || '')

    if (/\d+/.test(num)) {
      handleUpdatePartner(data.id, { max_daily_extra: parseInt(num) })
    } else if (num === '' && data.max_daily_extra !== undefined) {
      handleUpdatePartner(data.id, { max_daily_extra: db.firestore.FieldValue.delete() })
    }
  }

  const sendBadge = send => {

    let badge = 'cyan'
    badge = send.status === 'deleted' ? 'grey' : badge
    badge = send.status === 'paused' ? 'red' : badge
    badge = send.status === 'sending' ? 'green' : badge

    return badge
  }

  const sendTitle = send => {

    let title = 'Next Send'
    title = ['paused', 'deleted'].includes(send.status) ? 'Last Send' : title
    title = send.status === 'sending' ? 'Leads Daily - Sent' : title

    return title
  }

  const sendMessage = send => {

    let message = moment(send.last_timestamp.toDate()).format('h:mm A')
    message = ['paused', 'deleted'].includes(send.status) ? moment(send.last_timestamp.toDate()).format('MM/DD/YY') : message
    message = send.status === 'sending' ? send.count : message

    return message
  }

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size='small'
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size='small' style={{ width: 90 }}>
            Reset
          </Button>
          <Button
            type='link'
            size='small'
            onClick={() => {
              confirm({ closeDropdown: false })
              setSearchText(selectedKeys[0])
              setSearchedColumn(dataIndex)
            }}
          >
            Filter
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    filteredValue: filters[dataIndex] || null,
    render: text =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = clearFilters => {
    clearFilters()
    setSearchText('')
  }

  const columns = [
    {
      width: 150,
      key: 'created_at',
      title: 'Shared on',
      dataIndex: 'created_at',
      defaultSortOrder: 'descend',
      sorter: (a, b) => moment(a.created_at).valueOf() - moment(b.created_at).valueOf(),
      filteredValue: null,
    }, {
      width: 225,
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
      filteredValue: null,
    }, {
      width: 100,
      key: 'entity',
      title: 'Entity',
      dataIndex: 'entity',
      sorter: (a, b) => a.entity.localeCompare(b.entity),
      filteredValue: null,
    }, {
      width: 150,
      key: 'shared_to',
      title: 'Shared with',
      dataIndex: 'shared_to',
      sorter: (a, b) => a.shared_to.localeCompare(b.shared_to),
      ...getColumnSearchProps('shared_to')
    }, {
      width: 150,
      key: 'received_from',
      title: 'Received from',
      dataIndex: 'received_from',
      sorter: (a, b) => a.received_from.localeCompare(b.received_from),
      ...getColumnSearchProps('received_from')
    }, {
      width: 150,
      key: 'offer',
      title: 'Offer',
      dataIndex: 'offer',
      sorter: (a, b) => a.offer.localeCompare(b.offer),
      ...getColumnSearchProps('offer')
    }, {
      width: 100,
      key: 'leadShare_value',
      title: 'Value (USD)',
      dataIndex: 'leadShare_value',
      sorter: (a, b) => a.leadShare_value - b.leadShare_value,
    }
  ]

  const dropdownItems = (partner) => [
    ['paused'].includes(partner.status) && {
      key: 'activate',
      label: (
        <div onClick={() => handleActivatePartner(partner)}>{hoursSinceLast(partner) > 24 ? 'Activate' : 'Resume'}</div >
      ),
    },
    ['paused'].includes(partner.status) && {
      key: 'send_now',
      label: (
        <div onClick={() => handleSendNowPartner(partner)}>Send Now</div>
      ),
    },
    ['active', 'sending'].includes(partner.status) && {
      key: 'pause',
      label: (
        <div onClick={() => handleUpdatePartner(partner.id, { status: 'paused' })}>Pause</div>
      ),
    },
    ['active', 'sending'].includes(partner.status) && {
      key: 'send_more',
      label: (
        <div onClick={() => handleSendMorePartner(partner)}>Send More</div>
      ),
    },
    {
      type: 'divider'
    },
    {
      key: 'edit',
      label: (
        <div onClick={() => setEditPartner({ ...partner, status: partner.status === 'sending' ? 'active' : partner.status })}>Configure</div>
      ),
    },
    {
      key: 'clone',
      label: (
        <div onClick={() => setEditPartner({
          ...partner,
          id: undefined,
          name: `${partner.name} (copy)`,
          status: 'paused',
          count: 0,
          total_count: 0,
          max_daily: undefined,
        })}>
          Clone
        </div>
      ),
    },
    partner.status !== 'deleted' && {
      key: 'delete',
      danger: true,
      label: (
        <Popconfirm
          title={`Delete ${partner.name}`}
          description="Are you sure?"
          onConfirm={() => handleUpdatePartner(partner.id, { status: 'deleted' })}
        >
          Delete
        </Popconfirm>
      ),
    },
  ]

  const BadgeRibbonText = ({ partner }) => (
    <Dropdown menu={{ items: dropdownItems(partner) }}>
      <Space style={{ cursor: "pointer" }}>
        {partner.status}
        <DownOutlined style={{ fontSize: "80%" }} />
      </Space>
    </Dropdown>
  )


  const items = [
    {
      key: '1',
      label: `Active Lead Shares (${leadShares.length})`, // `header` becomes `label`
      children: (
        <Row gutter={[16, 16]}>
          {leadShares.sort((a, b) => b.length - a.length).map((x, i) => (
            <Col key={i} xs={24} sm={12} lg={8} xl={6} xxl={4}>
              <Badge.Ribbon text={<BadgeRibbonText partner={x} />} color={sendBadge(x)}>
                <Card title={<Tooltip title={`${x.name} (${x.id})`}>{x.name}</Tooltip>}>
                  <Statistic title="Leads Sent - Total" value={x.total_count} />
                  <Statistic title={x.max_daily_extra ? "Leads Daily - Max + Extra" : "Leads Daily - Maximum"} value={x.max_daily + (x.max_daily_extra || 0)} />
                  <Statistic title={sendTitle(x)} value={sendMessage(x)} />
                  <Progress percent={x.percent} status="active" />
                </Card>
              </Badge.Ribbon>
            </Col>
          ))}
        </Row>
      ),
    },
    {
      key: '2',
      label: `Paused Lead Shares (${leadSharesPaused.length})`,
      children: (
        <Row gutter={[16, 16]}>
          {leadSharesPaused.sort((a, b) => b.length - a.length).map((x, i) => (
            <Col key={i} xs={24} sm={12} lg={8} xl={6} xxl={4}>
              <Badge.Ribbon text={<BadgeRibbonText partner={x} />} color={sendBadge(x)}>
                <Card title={<Tooltip title={`${x.name} (${x.id})`}>{x.name}</Tooltip>}>
                  <Statistic title="Leads Sent - Total" value={x.total_count} />
                  <Statistic title="Leads Daily - Maximum" value={x.max_daily} />
                  <Statistic title={sendTitle(x)} value={sendMessage(x)} />
                  <Progress percent={x.percent} status="active" />
                </Card>
              </Badge.Ribbon>
            </Col>
          ))}
        </Row>
      ),
    },
  ]

  isTestUser && items.push({
    key: '3',
    label: `Deleted Lead Shares (${leadSharesDeleted.length})`,
    children: (
      <Row gutter={[16, 16]}>
        {leadSharesDeleted.sort((a, b) => b.length - a.length).map((x, i) => (
          <Col key={i} xs={24} sm={12} lg={8} xl={6} xxl={4}>
            <Badge.Ribbon text={<BadgeRibbonText partner={x} />} color={sendBadge(x)}>
              <Card title={<Tooltip title={`${x.name} (${x.id})`}>{x.name}</Tooltip>}>
                <Statistic title="Leads Sent - Total" value={x.total_count} />
                <Statistic title="Leads Daily - Maximum" value={x.max_daily} />
                <Statistic title={sendTitle(x)} value={sendMessage(x)} />
                <Progress percent={x.percent} status="active" />
              </Card>
            </Badge.Ribbon>
          </Col>
        ))}
      </Row>
    ),
  })

  const determineAllUnqiue = list => {

    const unique = new Set()

    list?.forEach((obj) => {
      unique.add(obj.email)
    })

    return unique.size
  }

  const handlePartnerModalOk = async (data) => {
    // debug
    // alert(JSON.stringify(data, undefined, ' '))

    try {
      await handleUpdatePartner(editPartner.id, data)
      setEditPartner(null)
    } catch (error) {
      console.warn('Error updating partner leadShare configuration', error)
      alert('Error updating partner leadShare configuration (see console for more details)')
    }
  }

  const handlePartnerModalCancel = (data) => {
    setEditPartner(null)
  }

  return (
    <>
      {editPartner &&
        <LeadShareParterModal partner={editPartner} onOk={handlePartnerModalOk} onCancel={handlePartnerModalCancel} />
      }

      <Title level={2}>Leads Shared</Title>
      <Text type='secondary' style={{ marginBottom: '1em' }}>Leads shared with external partners</Text>
      <Row gutter={[16, 16]} justify='end' align='center'>
        <Col sm={8} md={4} >
          <Button
            type='primary'
            loading={isFetching || isFetchingTable}
            onClick={handleRefresh}
          >
            Refresh Data
          </Button>
        </Col>
      </Row>

      {(isFetching || isFetchingTable) &&
        <Skeleton active={true} paragraph={{ rows: 1 }} />
      }

      {!(isFetching || isFetchingTable) &&
        <Row gutter={[16, 16]} justify='space-between' align='middle'>
          <Col xm={24} sm={16} style={{ display: 'flex', flexDirection: 'column' }}>
            <DateRangeSelector fetchStartDate={fetchStartDate} fetchEndDate={fetchEndDate} onChange={({ startDate, endDate }) => {
              resetFilters();
              setFetchStartDate(startDate)
              setFetchEndDate(endDate)
            }} />
          </Col>
        </Row>
      }

      {historyTable.length > 0 &&
        <Row gutter={[16, 16]}>
          <Col>
            <Card variant='borderless' style={{ opacity: filters.shared_to || filters.received_from ? 0.3 : 1 }}>
              <Statistic
                title={<span>Total{!filters.shared_to && !filters.received_from && " Shared"} </span>}
                value={historyTable.length} />
            </Card>
          </Col>
          {filters.received_from && filters.shared_to &&
            <Col>
              <Card variant='borderless' style={{ opacity: 0.3 }}>
                <Statistic
                  title={<span>Received From {filters.received_from && <FilterOutlined />}</span>}
                  value={totalReceived}
                />
              </Card>
            </Col>
          }
          {filters.received_from && filters.shared_to &&
            <Col>
              <Card variant='borderless' style={{ opacity: 0.3 }}>
                <Statistic
                  title={<span>Shared With {filters.shared_to && <FilterOutlined />}</span>}
                  value={totalSent}
                />
              </Card>
            </Col>
          }
          {(filters.shared_to || filters.received_from) &&
            <Col>
              <Card variant='borderless'>
                <Statistic
                  title={<span>Total Shared <FilterOutlined /></span>}
                  value={totalReceivedAndSent}
                />
              </Card>
            </Col>
          }
          <Col>
            <Card variant='borderless'>
              <Statistic
                title={<span>Unique Shared {(filters.received_from || filters.shared_to) && <FilterOutlined />}</span>}
                value={`${new Intl.NumberFormat().format(totalUniqueSent)} (${totalUniqueSentPercent?.toFixed()}%)`}
              />
            </Card>
          </Col>
        </Row>
      }

      {groupedPartners.length > 0 &&
        <>
          <Divider orientation='left'>Shared lead with<Text type='secondary' style={{ marginBottom: '1em', fontWeight: 'normal' }}> - <a onClick={() => toggleFilter('shared_to', undefined)}>Select all</a> | <a onClick={() => toggleFilter('shared_to', [])} >Clear</a></Text></Divider>
          <Row gutter={[16, 16]}>
            {groupedPartners.sort((a, b) => b.length - a.length).map((x, i) =>
              <Col key={i} xs={24} sm={12} lg={8} xl={6} xxl={4}>
                <Card
                  size='small'
                  hoverable={true}
                  title={<Tooltip title={`${x[0].full_name} (shared_to ${x[0].shared_to} entity ${x[0].entity})`}>{x[0].full_name}</Tooltip>}
                  style={{ cursor: 'pointer', opacity: !filters.shared_to || filters.shared_to?.includes(x[0].full_name) ? "1" : "0.3" }}
                  onClick={() => toggleFilter('shared_to', x[0].full_name)}
                >
                  <Statistic title='Total' value={filters.received_from
                    ? partnerTotals[x[0].full_name]?.total
                    : x.length
                  } />
                </Card>
              </Col>
            )}
          </Row>
        </>
      }

      {groupedReceived.length > 0 &&
        <>
          <Divider orientation='left'>Received lead from<Text type='secondary' style={{ marginBottom: '1em', fontWeight: 'normal' }}> - <a onClick={() => toggleFilter('received_from', undefined)}>Select all</a> | <a onClick={() => toggleFilter('received_from', [])} >Clear</a></Text></Divider>
          <Row gutter={[16, 16]}>
            {groupedReceived.sort((a, b) => b.length - a.length).map((x, i) =>
              <Col key={i} xs={24} sm={12} lg={8} xl={6} xxl={4}>
                <Card
                  size='small'
                  hoverable={true}
                  title={<Tooltip title={`${x[0].received_from} (affiliate ${x[0].affiliate_id}${x[0].affIdHashed ? `/${x[0].affIdHashed}` : ''})`}>{x[0].received_from}</Tooltip>}
                  style={{ cursor: 'pointer', opacity: !filters.received_from || filters.received_from?.includes(x[0].received_from) ? "1" : "0.3" }}
                  onClick={() => toggleFilter('received_from', x[0].received_from)}
                >
                  <Statistic title='Total' value={receivedTotals[x[0].received_from]?.total} />
                  <Statistic title='Unique' value={`${new Intl.NumberFormat().format(receivedTotals[x[0].received_from]?.unique || 0)} (${(receivedTotals[x[0].received_from]?.uniquePct || 0).toFixed()}%)`} />
                </Card>
              </Col>
            )}
          </Row>
        </>
      }

      {historyTable.length > 0 &&
        <Row justify='space-between' align='middle'>
          <Table
            style={{ paddingTop: 20 }}
            onChange={(pagination, filters, sorter, extra) => {
              setFilters(filters)
              applyFilters(historyTable, filters)
            }}
            loading={isFetching || isFetchingTable}
            columns={columns}
            dataSource={filteredTable}
            scroll={{ x: 1000, y: 500 }}
            size='small'
            title={() => (
              <CSVLink
                filename={'LeadShare.csv'}
                data={filteredTable}
                className='btn btn-primary'
              >
                Export to CSV
              </CSVLink>
            )}
          />
        </Row>
      }

      <Divider />

      {!isFetching &&
        <Collapse defaultActiveKey={['1']} accordion ghost items={items} />
      }
    </>
  )
}

export default LeadsShared